import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/sonner'
import { caseSchemaStatusKey } from '@/features/schema/hooks/useCaseSchemaStatus'
import { ApiError } from '@/services/api/client'
import {
  type SchemaClarificationAnswerBody,
  type SchemaSession,
  type SchemaSessionStartBody,
  schemaSessionsApi,
} from '@/services/api/schemaSessions'
import { schemaErrorMessage } from '../errors'
import { schemaSessionKeys } from './use-schema-session'

function movedHead(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || typeof error.data !== 'object' || !error.data)
    return undefined
  const detail = (error.data as { detail?: { code?: string; headId?: string | null } }).detail
  return detail?.code === 'schema_head_moved' ? (detail.headId ?? 'none') : undefined
}

export function useWorkbench(session: SchemaSession, onAccepted: (id: string) => Promise<void>) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [headWarning, setHeadWarning] = useState<string>()
  const update = useCallback(
    (next: SchemaSession | null) => {
      qc.setQueryData(schemaSessionKeys.current(session.caseId), next)
    },
    [qc, session.caseId]
  )
  const execute = async (action: () => Promise<SchemaSession | null>) => {
    setBusy(true)
    try {
      update(await action())
      return true
    } catch (error) {
      toast.error(schemaErrorMessage(error, t('schemaAgent.workbench.error')))
      return false
    } finally {
      setBusy(false)
    }
  }
  const accept = async (confirmedHeadId?: string) => {
    setBusy(true)
    try {
      const next = await schemaSessionsApi.accept(session.caseId, session.id, { confirmedHeadId })
      update(next)
      await qc.invalidateQueries({ queryKey: caseSchemaStatusKey(session.caseId) })
      setHeadWarning(undefined)
      if (next.acceptedSchemaId) await onAccepted(next.acceptedSchemaId)
      toast.success(t('schemaAgent.review.accepted'))
    } catch (error) {
      const head = movedHead(error)
      if (head !== undefined) setHeadWarning(head)
      else toast.error(schemaErrorMessage(error, t('schemaAgent.review.acceptError')))
    } finally {
      setBusy(false)
    }
  }
  return {
    busy,
    update,
    headWarning,
    setHeadWarning,
    accept,
    resolve: (apply: boolean) =>
      execute(() => schemaSessionsApi.resolveProposal(session.caseId, session.id, apply)),
    discard: () =>
      execute(async () => {
        await schemaSessionsApi.discard(session.caseId, session.id)
        return null
      }),
    repair: () => execute(() => schemaSessionsApi.repair(session.caseId, session.id)),
    answer: (answers: SchemaClarificationAnswerBody['answers']) =>
      execute(() => schemaSessionsApi.answer(session.caseId, session.id, { answers })),
    regenerate: (body: SchemaSessionStartBody) =>
      execute(async () => {
        update(await schemaSessionsApi.updateIntake(session.caseId, session.id, body))
        return schemaSessionsApi.regenerate(session.caseId, session.id)
      }),
  }
}
